import React, { useState } from 'react'
import styled from 'styled-components'
import { PageHeader } from 'components'
import { AppPrimaryButton, AppSecondaryButton } from 'elements'
import {
    ACCOUNT_CHECKOUT_PATH,
    ACCOUNT_PLAN_PATH,
    CONTACT_SALES_URL,
    ENABLE_LICENSE_SHOPPING_CART,
} from 'utility/subscription-usage'
import { useNavigate } from 'react-router-dom'

const Page = styled.div`
    padding: 8px 4px 32px;
    .back {
        width: auto;
        height: auto !important;
        margin-bottom: 12px;
    }
    @media (max-width: 930px) {
        padding: 4px 0 24px;
    }
`

const Breadcrumb = styled.p`
    font-size: 13px;
    color: #6b7280;
    margin: 4px 0 8px;
    strong {
        color: #111827;
        font-weight: 600;
    }
`

const Intro = styled.p`
    font-size: 15px;
    color: #4b5563;
    margin: 0 0 20px;
`

const BillingToggle = styled.div`
    display: inline-flex;
    background: #f3f4f6;
    border-radius: 999px;
    padding: 4px;
    margin-bottom: 20px;
    button {
        border: none;
        background: transparent;
        padding: 8px 16px;
        border-radius: 999px;
        font-size: 13px;
        font-weight: 600;
        color: #6b7280;
        cursor: pointer;
    }
    button.active {
        background: #fff;
        color: #111827;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
    }
    .save {
        margin-left: 6px;
        font-size: 11px;
        color: #047857;
        font-weight: 700;
    }
`

const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 16px;
    @media (max-width: 1100px) {
        grid-template-columns: 1fr;
    }
`

const Card = styled.div`
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 22px 20px;
    display: flex;
    flex-direction: column;
    position: relative;
    &.featured {
        border-color: #2563eb;
        box-shadow: 0 0 0 1px #2563eb;
    }
    .badge {
        position: absolute;
        top: -10px;
        right: 16px;
        background: #2563eb;
        color: #fff;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 0.04em;
        text-transform: uppercase;
        padding: 3px 8px;
        border-radius: 999px;
    }
    .name {
        font-size: 22px;
        font-weight: 700;
        color: #111827;
        margin: 0 0 6px;
    }
    .blurb {
        font-size: 13px;
        color: #6b7280;
        line-height: 1.5;
        min-height: 40px;
        margin: 0 0 16px;
    }
    .price {
        font-size: 28px;
        font-weight: 700;
        color: #111827;
        margin-bottom: 16px;
        .unit {
            font-size: 14px;
            font-weight: 500;
            color: #6b7280;
        }
    }
    ul {
        list-style: none;
        padding: 0;
        margin: 16px 0 0;
        flex: 1;
    }
    li {
        font-size: 13px;
        color: #374151;
        padding: 6px 0;
        padding-left: 18px;
        position: relative;
    }
    li::before {
        content: '✓';
        position: absolute;
        left: 0;
        color: #2563eb;
        font-weight: 700;
    }
    .cta {
        margin-top: 8px;
        width: 100%;
        height: auto !important;
    }
`

const LiteCard = styled(Card)`
    margin-top: 16px;
    @media (min-width: 1101px) {
        grid-column: 1 / -1;
        display: grid;
        grid-template-columns: 1fr 1fr auto;
        gap: 16px;
        align-items: start;
        .price {
            margin-bottom: 0;
        }
        ul {
            margin-top: 0;
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 0 16px;
        }
        .cta {
            align-self: center;
            width: auto;
            min-width: 148px;
            height: auto !important;
        }
    }
`

const Compare = styled.div`
    margin-top: 28px;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    overflow: hidden;
    h3 {
        font-size: 16px;
        font-weight: 700;
        margin: 0;
        padding: 16px 20px;
        border-bottom: 1px solid #e5e7eb;
    }
    table {
        width: 100%;
        border-collapse: collapse;
        font-size: 13px;
    }
    th,
    td {
        padding: 10px 16px;
        text-align: left;
        border-bottom: 1px solid #f3f4f6;
    }
    th {
        color: #6b7280;
        font-weight: 600;
        background: #f9fafb;
    }
    td:first-child {
        color: #111827;
        font-weight: 500;
    }
    @media (max-width: 930px) {
        overflow-x: auto;
        table {
            min-width: 520px;
        }
    }
`

const PREMIUM_PRICE = { monthly: '12.99', yearly: '9.99' }
const LITE_PRICE = { monthly: '6', yearly: '5' }

const FREE_FEATURES = [
    'Up to 10 free seats',
    '5 active templates, unlimited inspections',
    'Analytics',
    'Training',
    'Tasks & issue reporting',
    'Up to 3 years of data & evidence history',
]

const PREMIUM_FEATURES = [
    'Unlimited paid seats',
    'Unlimited active inspection templates',
    'Advanced analytics',
    'Advanced training',
    'Permissions & access management',
    'Out-of-the-box & custom integrations',
    'Advanced security, including SSO & SCIM',
    'Unlimited data & evidence history',
]

const ENTERPRISE_FEATURES = [
    'Per seat, best for large enterprise rollouts',
    'Site based, best for large store, property or multi-location rollouts',
    'Pricing & services tailored to large organizations',
]

const LITE_FEATURES = [
    'Create & complete training',
    'Issues, actions & assets',
    'Heads Up notifications',
    '12 inspections / year',
    'View-only analytics & reports',
]

const COMPARE_ROWS = [
    ['Active templates', 'Up to 5', 'Unlimited'],
    ['Inspections', 'Unlimited', 'Unlimited'],
    ['Reports', 'PDF, Web', 'PDF, Word, Excel, Web'],
    ['Approval workflow', '–', 'Yes'],
    ['Custom branded reports', '–', 'Yes'],
    ['Integrations', '–', 'Yes'],
    ['SSO & SCIM', '–', 'Yes'],
]

export const AccountPricingScreen = () => {
    const navigate = useNavigate()
    const [billing, setBilling] = useState('yearly')
    const premium = PREMIUM_PRICE[billing]
    const lite = LITE_PRICE[billing]

    return (
        <Page>
            <AppSecondaryButton
                type="button"
                className="back"
                onClick={() => navigate(ACCOUNT_PLAN_PATH)}
            >
                ← Back
            </AppSecondaryButton>
            <PageHeader header="Plans & Pricing" />
            <Breadcrumb>
                Settings › Account & Plan › <strong>Pricing</strong>
            </Breadcrumb>
            <Intro>Find the right plan for your team.</Intro>
            <BillingToggle>
                <button
                    type="button"
                    className={billing === 'monthly' ? 'active' : ''}
                    onClick={() => setBilling('monthly')}
                >
                    Monthly
                </button>
                <button
                    type="button"
                    className={billing === 'yearly' ? 'active' : ''}
                    onClick={() => setBilling('yearly')}
                >
                    Yearly
                    <span className="save">24% off</span>
                </button>
            </BillingToggle>
            <Grid>
                <Card>
                    <h2 className="name">Free</h2>
                    <p className="blurb">Everything you need to get started</p>
                    <div className="price">
                        $0 <span className="unit">/ seat / month</span>
                    </div>
                    <AppSecondaryButton
                        type="button"
                        className="cta"
                        onClick={() => navigate(ACCOUNT_PLAN_PATH)}
                    >
                        Current plan
                    </AppSecondaryButton>
                    <ul>
                        {FREE_FEATURES.map(item => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                </Card>
                <Card className="featured">
                    <span className="badge">Best value</span>
                    <h2 className="name">Premium</h2>
                    <p className="blurb">
                        Improve safety, quality, and operations with advanced
                        tools for growing teams
                    </p>
                    <div className="price">
                        ${premium} <span className="unit">/ seat / month</span>
                    </div>
                    {ENABLE_LICENSE_SHOPPING_CART && (
                        <AppPrimaryButton
                            type="button"
                            className="cta"
                            onClick={() => navigate(ACCOUNT_CHECKOUT_PATH)}
                        >
                            Purchase Premium
                        </AppPrimaryButton>
                    )}
                    <ul>
                        {PREMIUM_FEATURES.map(item => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                </Card>
                <Card>
                    <h2 className="name">Enterprise</h2>
                    <p className="blurb">
                        Pricing & services tailored to maximize impact across
                        large organizations
                    </p>
                    <div className="price">Custom</div>
                    <AppPrimaryButton
                        type="button"
                        className="cta"
                        onClick={() =>
                            window.location.assign(CONTACT_SALES_URL)
                        }
                    >
                        Contact sales
                    </AppPrimaryButton>
                    <ul>
                        {ENTERPRISE_FEATURES.map(item => (
                            <li key={item}>{item}</li>
                        ))}
                    </ul>
                </Card>
            </Grid>
            <LiteCard>
                <div>
                    <h2 className="name">Lite seat</h2>
                    <p className="blurb">
                        Add Lite seats to any paid plan. Affordable access for
                        occasional users.
                    </p>
                    <div className="price">
                        From ${lite}{' '}
                        <span className="unit">/ seat / month</span>
                    </div>
                </div>
                <ul>
                    {LITE_FEATURES.map(item => (
                        <li key={item}>{item}</li>
                    ))}
                </ul>
                <AppPrimaryButton
                    type="button"
                    className="cta"
                    onClick={() => window.location.assign(CONTACT_SALES_URL)}
                >
                    Contact sales
                </AppPrimaryButton>
            </LiteCard>
            <Compare>
                <h3>Compare plan features</h3>
                <table>
                    <thead>
                        <tr>
                            <th>Feature</th>
                            <th>Free</th>
                            <th>Premium</th>
                        </tr>
                    </thead>
                    <tbody>
                        {COMPARE_ROWS.map(row => (
                            <tr key={row[0]}>
                                <td>{row[0]}</td>
                                <td>{row[1]}</td>
                                <td>{row[2]}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Compare>
        </Page>
    )
}
